import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, LogOut, Plus, Printer, Settings2, Pencil, Megaphone, X, Copy, Mail, ArrowLeft, CreditCard, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { clearToken } from "../../lib/authStorage";
import TomorrowGlance from "../../components/TomorrowGlance";
import { ClockToggle } from "../../lib/clockFormat";
import { copyToClipboard } from "../../lib/clipboard";
import { TimetableGrid } from "../../components/parent/TimetableGrid";
import { ScheduleEditor } from "../../components/parent/ScheduleEditor";
import { CalendarPanel } from "../../components/parent/CalendarPanel";
import { Paywall } from "../../components/parent/Paywall";
import { SlotEditorModal } from "../../components/parent/SlotEditorModal";
import { NoteEditorModal } from "../../components/parent/NoteEditorModal";
import { AddChildModal } from "../../components/parent/AddChildModal";
import { ChildEditModal } from "../../components/parent/ChildEditModal";

export default function ParentHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [currentCid, setCurrentCid] = useState("");
  const [entries, setEntries] = useState({});
  const [notes, setNotes] = useState({});
  const [editingSlot, setEditingSlot] = useState(null);
  const [noteDay, setNoteDay] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [schedDraft, setSchedDraft] = useState(null);
  const [paying, setPaying] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [view, setView] = useState("timetable");
  const [showAddChild, setShowAddChild] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [showReferral, setShowReferral] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [showFamily, setShowFamily] = useState(false);
  const [cpForm, setCpForm] = useState({ name: "", email: "", password: "" });
  const [cpBusy, setCpBusy] = useState(false);

  const hydrate = (cid, children) => {
    const c = (children || []).find((x) => x.id === cid) || (children || [])[0];
    if (!c) return;
    setCurrentCid(c.id);
    setEntries(c.timetable || {});
    setNotes(c.notes || {});
    setSchedDraft(c.schedule);
    setEditingSlot(null);
    setNoteDay(null);
    setEditingSchedule(false);
  };

  const load = async (keepCid) => {
    try {
      const { data } = await api.get("/parent/bootstrap");
      setData(data);
      hydrate(keepCid || currentCid, data.children);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="min-h-screen flex items-center justify-center font-display font-black text-2xl animate-pulse" data-testid="parent-loading">Loading…</div>;

  const children = data.children || [];
  const child = children.find((c) => c.id === currentCid) || children[0];
  if (!child) return <div className="min-h-screen flex items-center justify-center font-display font-bold text-xl" data-testid="parent-no-child">No timetable found on this account.</div>;

  const isStudent = (data.parent.account_type || "parent") === "student";
  const yourLC = isStudent ? "your" : `${child.name}'s`;
  const YourUC = isStudent ? "Your" : `${child.name}'s`;
  const { days, slots } = child.schedule;
  const dayOverrides = child.schedule.day_overrides || {};
  const subscribed = data.billing_ok;
  const plans = data.parent_plans || [];
  const allowance = data.child_allowance || 1;
  const canAddChild = children.length < allowance;
  const addon = data.addon_plan;
  const childExams = (data.exams || []).filter((e) => e.child_id === child.id);
  const referralText = `Hi,\n\nI've been using MyTimetable.co.uk to keep track of ${isStudent ? "my" : "my child's"} timetable, exams and school notes. There's a free school portal where teachers, classes and rooms are pre-loaded, so pupils build their own timetables and get notices and homework reminders.\n\nMight be worth a look: ${window.location.origin}\n\nThanks!`;

  const subscribe = async (p) => {
    setPaying(p.id);
    try {
      localStorage.setItem("timely_pay_return", "/parent");
      const { data } = await api.post("/payments/checkout", { plan_id: p.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setPaying("");
    }
  };

  const buyAddon = async () => {
    if (!addon) return toast.error("Add-on unavailable right now — please try again later");
    setPaying("addon");
    try {
      localStorage.setItem("timely_pay_return", "/parent");
      const { data } = await api.post("/payments/checkout", { plan_id: addon.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setPaying("");
    }
  };

  const redeem = async () => {
    if (!voucherCode.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/parent/redeem", { code: voucherCode });
      toast.success(`Voucher applied — free access for ${data.years} year${data.years > 1 ? "s" : ""}!`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRedeeming(false);
    }
  };

  const slotFor = (day, idx) => (dayOverrides[day]?.slots || slots)[idx];

  const openSlot = (day, idx) => {
    if (!subscribed) return toast.error("Subscribe to unlock the timetable builder");
    setEditingSlot({ day, idx });
  };

  const openNote = (d) => {
    if (!subscribed) return toast.error("Subscribe to add notes");
    setNoteDay(d);
  };

  const persistEntries = async (next, msg) => {
    setEntries(next);
    setEditingSlot(null);
    try {
      await api.put(`/parent/children/${child.id}/timetable`, { entries: next });
      toast.success(msg);
      if (!isStudent && Object.keys(next).length === 1 && !localStorage.getItem("timely_upsell_dismissed")) setShowUpsell(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveSlotEntry = (form) => {
    const key = `${editingSlot.day}:${editingSlot.idx}`;
    const next = { ...entries };
    if (form.subject || form.teacher || form.room) next[key] = form;
    else delete next[key];
    persistEntries(next, "Timetable updated");
  };

  const clearSlotEntry = () => {
    const next = { ...entries };
    delete next[`${editingSlot.day}:${editingSlot.idx}`];
    persistEntries(next, "Slot cleared");
  };

  const saveNote = async (text) => {
    const next = { ...notes };
    if (text.trim()) next[noteDay] = text;
    else delete next[noteDay];
    setNotes(next);
    setNoteDay(null);
    try {
      await api.put(`/parent/children/${child.id}/notes`, { notes: next });
      toast.success("Note saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveSchedule = async () => {
    try {
      await api.put(`/parent/children/${child.id}/schedule`, schedDraft);
      toast.success("Schedule saved");
      setEditingSchedule(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const manageBilling = async () => {
    try {
      const { data: res } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = res.portal_url;
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addCoParent = async () => {
    if (!cpForm.name.trim() || !cpForm.email.trim()) return toast.error("Add their name and email");
    if (cpForm.password.length < 8) return toast.error("Password needs at least 8 characters");
    setCpBusy(true);
    try {
      await api.post("/parent/co-parents", cpForm);
      toast.success(`${cpForm.name} can now log in with their own email & password`);
      setCpForm({ name: "", email: "", password: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCpBusy(false);
    }
  };

  const removeCoParent = async (id) => {
    try {
      await api.delete(`/parent/co-parents/${id}`);
      toast.success("Access removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="min-h-screen" data-testid="parent-home">
      {user.impersonated && (
        <div className="bg-[#D8B4FE] border-b-2 border-zinc-900 px-4 py-2 flex items-center justify-between gap-3" data-testid="impersonation-banner">
          <span className="font-bold text-sm">You are viewing as <span className="font-display font-black">{user.name}</span> (super-admin mode)</span>
          <button onClick={() => { clearToken("parent"); window.location.href = "/super/parents"; }} data-testid="exit-impersonation-btn" className="nb-btn bg-white px-3 py-1.5 text-xs shrink-0">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to super admin
          </button>
        </div>
      )}
      <header className="border-b-2 border-zinc-900 bg-[#F9A8D4] sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 shrink-0 rounded-xl border-2 border-zinc-900 bg-white flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
              <Clock className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-black text-lg leading-tight truncate" data-testid="parent-greeting">{YourUC} timetable</div>
              <div className="text-xs font-bold text-zinc-700 truncate">{isStudent ? user.email : `Managed by ${user.name}`}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 no-print">
            <button onClick={() => setShowReferral(true)} data-testid="referral-btn" className="nb-btn bg-white px-3 py-2 text-sm">
              <Megaphone className="w-4 h-4" /> <span className="hidden sm:inline">Tell your school</span>
            </button>
            {!isStudent && (
              <button onClick={() => setShowFamily(true)} data-testid="family-btn" className="nb-btn bg-white px-3 py-2 text-sm">
                <Users className="w-4 h-4" /> <span className="hidden sm:inline">Family</span>
              </button>
            )}
            {subscribed && !data.parent.fees_waived && (
              <button onClick={manageBilling} data-testid="manage-billing-btn" className="nb-btn bg-white px-3 py-2 text-sm">
                <CreditCard className="w-4 h-4" /> <span className="hidden sm:inline">Billing</span>
              </button>
            )}
            <ClockToggle className="nb-btn bg-white px-3 py-2 text-sm font-bold" />
            <button onClick={() => window.print()} data-testid="parent-print-btn" className="nb-btn bg-white px-3 py-2 text-sm">
              <Printer className="w-4 h-4" /> <span className="hidden sm:inline">Print</span>
            </button>
            {subscribed && (
              <button onClick={() => setEditingSchedule(!editingSchedule)} data-testid="edit-schedule-toggle" className="nb-btn bg-white px-3 py-2 text-sm">
                <Settings2 className="w-4 h-4" /> <span className="hidden sm:inline">Edit times</span>
              </button>
            )}
            <button onClick={() => { logout(); navigate("/"); }} data-testid="parent-logout-btn" className="nb-btn bg-white px-3 py-2 text-sm">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="hidden print:block font-display font-black text-xl mb-3">{YourUC} timetable</div>

        {!isStudent && (
          <div className="flex flex-wrap items-center gap-2 mb-4 no-print" data-testid="child-tabs">
            {children.map((c) => (
              <span key={c.id} className={`inline-flex items-center gap-1 rounded-xl border-2 border-zinc-900 transition-colors duration-150 ${c.id === child.id ? "bg-[#F9A8D4]" : "bg-white"}`}>
                <button onClick={() => hydrate(c.id, children)} data-testid={`child-tab-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="pl-3 pr-2 py-1.5 font-display font-bold text-sm">{c.name}</button>
                {c.id === child.id && (
                  <button onClick={() => setEditingChild(c)} data-testid="child-edit-btn"
                    className="pr-2 hover:text-zinc-500 transition-colors duration-150"><Pencil className="w-3.5 h-3.5" /></button>
                )}
              </span>
            ))}
            {canAddChild ? (
              <button onClick={() => setShowAddChild(true)} data-testid="add-child-btn" className="nb-btn bg-white px-3 py-1.5 text-xs">
                <Plus className="w-3.5 h-3.5" /> Add child
              </button>
            ) : subscribed && addon ? (
              <button onClick={buyAddon} disabled={!!paying} data-testid="buy-child-slot-btn" className="nb-btn bg-[#FDE68A] px-3 py-1.5 text-xs">
                <Plus className="w-3.5 h-3.5" /> {paying === "addon" ? "Redirecting…" : `Add a child — £${Number(addon.price_gbp).toFixed(2)}${addon.mode === "one_time" ? " one-off" : "/yr"}`}
              </button>
            ) : null}
          </div>
        )}

        {showUpsell && !isStudent && (
          <div className="nb-card p-4 mb-4 bg-[#FDE68A] flex items-center justify-between gap-3 flex-wrap no-print" data-testid="sibling-upsell">
            <p className="font-bold text-sm">Looking good! Add a brother or sister's timetable too — extra children are £5/year each (or £20 each on the 6-year plan).</p>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => { setShowUpsell(false); if (canAddChild) { setShowAddChild(true); } else { buyAddon(); } }} data-testid="sibling-upsell-cta" className="nb-btn bg-[#1E3A8A] text-white px-3 py-1.5 text-xs">Add a child</button>
              <button onClick={() => { setShowUpsell(false); localStorage.setItem("timely_upsell_dismissed", "1"); }} data-testid="sibling-upsell-dismiss" className="nb-btn bg-white px-3 py-1.5 text-xs">Not now</button>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-6 no-print" data-testid="parent-view-tabs">
          {[["timetable", "Timetable"], ["calendar", "Calendar & school year"]].map(([v, label]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`parent-view-${v}`}
              className={`nb-btn px-5 py-2 text-sm ${view === v ? "bg-[#F9A8D4]" : "bg-white text-zinc-600"}`}>
              {label}
            </button>
          ))}
        </div>

        {!subscribed && (
          <Paywall plans={plans} paying={paying} onSubscribe={subscribe} yourLC={yourLC} isStudent={isStudent}
            voucherCode={voucherCode} setVoucherCode={setVoucherCode} redeeming={redeeming} onRedeem={redeem} />
        )}

        {editingSchedule && subscribed && schedDraft && (
          <ScheduleEditor draft={schedDraft} setDraft={setSchedDraft} onSave={saveSchedule} />
        )}

        {view === "timetable" && (
          <>
            <TomorrowGlance schedule={child.schedule} entries={entries} notes={notes} exams={childExams}
              resolveEntry={(e) => ({ title: e.subject || "Class", sub: [e.teacher, e.room].filter(Boolean).join(" · ") })} />
            <TimetableGrid days={days} slots={slots} dayOverrides={dayOverrides} entries={entries} notes={notes}
              subscribed={subscribed} onSlot={openSlot} onNote={openNote} />
          </>
        )}

        {view === "calendar" && (
          <CalendarPanel child={child} exams={childExams} isStudent={isStudent} subscribed={subscribed} reload={() => load()} />
        )}
      </main>

      {editingSlot && (
        <SlotEditorModal key={`${editingSlot.day}:${editingSlot.idx}`} day={editingSlot.day}
          slot={slotFor(editingSlot.day, editingSlot.idx)} entry={entries[`${editingSlot.day}:${editingSlot.idx}`]}
          onSave={saveSlotEntry} onClear={clearSlotEntry} onClose={() => setEditingSlot(null)} />
      )}

      {noteDay && (
        <NoteEditorModal key={noteDay} noteDay={noteDay} initialText={notes[noteDay] || ""}
          onSave={saveNote} onClose={() => setNoteDay(null)} />
      )}

      {showAddChild && (
        <AddChildModal onClose={() => setShowAddChild(false)}
          onAdded={(nc) => { setShowAddChild(false); load(nc.id); }} />
      )}

      {showReferral && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="referral-modal">
          <div className="nb-card p-6 w-full max-w-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl flex items-center gap-2"><Megaphone className="w-5 h-5" /> Tell your school about us</h3>
              <button onClick={() => setShowReferral(false)} data-testid="referral-close" className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-zinc-600 mb-3">Schools get their own portal — teachers, rooms and classes pre-loaded so pupils build timetables in minutes (free to trial). Send this to the school office:</p>
            <textarea readOnly className="nb-input min-h-[150px] resize-y text-sm" data-testid="referral-text" value={referralText} />
            <div className="flex gap-3 mt-4">
              <button onClick={async () => { const ok = await copyToClipboard(referralText); ok ? toast.success("Copied!") : toast.error("Couldn't copy — select the text manually"); }} data-testid="referral-copy-btn" className="nb-btn flex-1 bg-[#A7E4D3] py-2.5"><Copy className="w-4 h-4" /> Copy message</button>
              <a href={`mailto:?subject=${encodeURIComponent("A timetable tool your parents are using")}&body=${encodeURIComponent(referralText)}`} data-testid="referral-mailto" className="nb-btn flex-1 bg-[#F9A8D4] py-2.5 justify-center"><Mail className="w-4 h-4" /> Open email</a>
            </div>
          </div>
        </div>
      )}

      {showFamily && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="family-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-xl">Family access</h3>
              <button onClick={() => setShowFamily(false)} data-testid="family-close" className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-zinc-600">Add another parent or carer — they log in at the same page with their own email and password, and see &amp; edit the same children and timetables.</p>
            <div className="mt-4 space-y-2" data-testid="co-parent-list">
              {(data.parent.co_parents || []).map((cp) => (
                <div key={cp.id} className="rounded-xl border-2 border-zinc-900 p-2.5 bg-[#FDFBF7] flex items-center justify-between gap-2" data-testid={`co-parent-${cp.id}`}>
                  <div className="min-w-0">
                    <div className="font-bold text-sm">{cp.name}</div>
                    <div className="text-[11px] text-zinc-500 font-semibold truncate">{cp.email}</div>
                  </div>
                  <button onClick={() => removeCoParent(cp.id)} data-testid={`co-parent-remove-${cp.id}`}
                    className="p-1.5 shrink-0 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150">
                    <Trash2 className="w-3.5 h-3.5 text-red-700" />
                  </button>
                </div>
              ))}
              {(data.parent.co_parents || []).length === 0 && (
                <p className="text-sm text-zinc-400 font-semibold" data-testid="co-parent-empty">No extra parents yet — add one below.</p>
              )}
            </div>
            <div className="mt-3 pt-3 border-t-2 border-zinc-100 space-y-2">
              <input className="nb-input" placeholder="Their name, e.g. Alex" data-testid="co-parent-name"
                value={cpForm.name} onChange={(e) => setCpForm({ ...cpForm, name: e.target.value })} maxLength={60} />
              <input className="nb-input" type="email" placeholder="Their email" data-testid="co-parent-email"
                value={cpForm.email} onChange={(e) => setCpForm({ ...cpForm, email: e.target.value })} />
              <input className="nb-input" type="password" placeholder="A password for them (min 8 chars)" data-testid="co-parent-password"
                value={cpForm.password} onChange={(e) => setCpForm({ ...cpForm, password: e.target.value })} />
              <button onClick={addCoParent} disabled={cpBusy} data-testid="co-parent-add" className="nb-btn w-full bg-[#A7E4D3] py-2.5 text-sm">
                <Users className="w-4 h-4" /> {cpBusy ? "Adding…" : "Add parent / carer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingChild && (
        <ChildEditModal child={editingChild} canDelete={children.length > 1}
          onClose={() => setEditingChild(null)}
          onChanged={(keepOpen) => { if (!keepOpen) setEditingChild(null); load(); }}
          onDeleted={() => { setEditingChild(null); load(""); }} />
      )}
    </div>
  );
}
